import { Radio, ShoppingBag } from 'lucide-react'
import { Button } from '../ui'
import type { NoAvailableApDialogContent } from '../../lib/floorPlan/rfRecommendationFixes'

type Props = {
  content: NoAvailableApDialogContent
  onClose: () => void
}

export default function FloorPlanNoApDialog({ content, onClose }: Props) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div
        className="w-full max-w-md rounded-xl border border-mg-border bg-mg-surface p-4 shadow-xl"
        role="dialog"
        aria-labelledby="no-ap-dialog-title"
        aria-describedby="no-ap-dialog-desc"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/50">
            <Radio size={20} className="text-amber-700 dark:text-amber-300" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="no-ap-dialog-title" className="text-sm font-semibold text-mg-text">
              {content.title}
            </h2>
            <p id="no-ap-dialog-desc" className="mt-1 text-sm font-medium text-amber-800 dark:text-amber-200">
              {content.message}
            </p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-amber-200/80 bg-amber-50/70 px-3 py-2.5 dark:border-amber-900/60 dark:bg-amber-950/35">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-800/80 dark:text-amber-200/80">
              To purchase
            </p>
            <p className="mt-0.5 text-2xl font-bold tabular-nums text-amber-900 dark:text-amber-100">
              {content.apShortfall}
              <span className="ml-1 text-sm font-semibold">
                AP{content.apShortfall !== 1 ? 's' : ''}
              </span>
            </p>
            <p className="mt-1 text-[10px] leading-relaxed text-amber-800/90 dark:text-amber-200/90">
              {content.spareCount > 0
                ? `${content.spareCount} spare in inventory — still ${content.apShortfall} short`
                : 'No spare APs in inventory'}
            </p>
          </div>
          <div className="rounded-lg border border-mg-border/80 bg-mg-bg/40 px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-mg-muted">
              For this floor
            </p>
            <p className="mt-0.5 text-2xl font-bold tabular-nums text-mg-text">
              {content.estimatedApsNeeded}
              <span className="ml-1 text-sm font-semibold">
                AP{content.estimatedApsNeeded !== 1 ? 's' : ''}
              </span>
            </p>
            <p className="mt-1 text-[10px] leading-relaxed text-mg-muted">
              Estimated to reach &lt;15% weak coverage
            </p>
          </div>
        </div>

        <ul className="mt-4 space-y-2 rounded-lg border border-mg-border/80 bg-mg-bg/40 px-3 py-3 text-[11px] leading-relaxed text-mg-muted">
          {content.details.map(line => (
            <li key={line} className="flex gap-2">
              <span className="mt-0.5 shrink-0 text-mg-accent">•</span>
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex items-center gap-2 rounded-lg border border-blue-200/80 bg-blue-50/50 px-3 py-2.5 dark:border-blue-900/60 dark:bg-blue-950/25">
          <ShoppingBag size={14} className="shrink-0 text-blue-600 dark:text-blue-300" />
          <p className="text-[11px] leading-relaxed text-blue-900 dark:text-blue-100">
            Contact your administrator or licensing portal to add{' '}
            {content.apShortfall} access point{content.apShortfall !== 1 ? 's' : ''} to your
            organization inventory.
          </p>
        </div>

        <div className="mt-4 flex justify-end">
          <Button variant="primary" size="sm" type="button" onClick={onClose}>
            Got it
          </Button>
        </div>
      </div>
    </div>
  )
}
